import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr'; // Importing the Navbar we created
import './style/Offers.css';

const ApprovedOffers = () => {
    const navigate = useNavigate();

    // --- State ---
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [offers, setOffers] = useState([]);

    // Filter State
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedCats, setSelectedCats] = useState([]);

    // Dropdown visibility state
    const [showTypeDrop, setShowTypeDrop] = useState(false);
    const [showCatDrop, setShowCatDrop] = useState(false);

    // Apply Loading State (per offer ID)
    const [applyingId, setApplyingId] = useState(null);

    // --- Helpers ---
    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    // --- Data Fetching ---
    useEffect(() => {
        const authToken = getCookie('authToken');

        const initData = async () => {
            try {
                setLoading(true);

                // 1. Check Auth
                const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'checkAuth',
                    authToken
                });

                if (authRes.data.status === false) {
                    toast.error(authRes.data.message || 'Session expired');
                    navigate('/login');
                    return;
                }

                setUser(authRes.data.res);

                // 2. Fetch Offers
                // 2. Fetch Approved Offers Only
                const offersRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'getApprovedOffers',
                    authToken
                });

                if (offersRes.data.status) {
                    setOffers(offersRes.data.offers || []);
                } else {
                    console.log('No approved offers found');
                }

            } catch (error) {
                console.error(error);
                toast.error('Failed to load data');
                // navigate('/login'); // Optional: redirect on hard fail
            } finally {
                setLoading(false);
            }
        };

        initData();
    }, [navigate]);

    // --- Derived Data for Filters ---
    const uniqueTypes = useMemo(() => [...new Set(offers.map(o => o.type))].filter(Boolean), [offers]);
    const uniqueCats = useMemo(() => [...new Set(offers.map(o => o.category))].filter(Boolean), [offers]);

    // --- Filter Logic ---
    const filteredOffers = useMemo(() => {
        return offers.filter(offer => {
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch =
                offer.name.toLowerCase().includes(searchLower) ||
                String(offer.id).includes(searchLower);

            const matchesType = selectedTypes.length === 0 || selectedTypes.includes(offer.type);
            const matchesCat = selectedCats.length === 0 || selectedCats.includes(offer.category);

            return matchesSearch && matchesType && matchesCat;
        });
    }, [offers, searchQuery, selectedTypes, selectedCats]);

    // --- Handlers ---
    const handleApply = async (offerId) => {
        setApplyingId(offerId);
        const authToken = getCookie('authToken');

        try {
            const response = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'applyOffer',
                authToken,
                offerId
            });

            if (response.data.status) {
                toast.success('Application sent successfully!');
                // Update local state to reflect pending status immediately
                setOffers(prev => prev.map(o =>
                    o.id === offerId ? { ...o, approved: 'pending' } : o
                ));
            } else {
                toast.error(response.data.message || 'Application failed');
            }
        } catch (error) {
            toast.error('Network error applying for offer');
        } finally {
            setApplyingId(null);
        }
    };

    const toggleFilter = (item, list, setList) => {
        if (list.includes(item)) {
            setList(list.filter(i => i !== item));
        } else {
            setList([...list, item]);
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="offers-page-wrapper-unique">
            {/* Background */}
            <div className="bg-shape-unique shape-1-unique"></div>
            <div className="bg-shape-unique shape-2-unique"></div>

            {/* Navbar */}
            <Navbar name={user?.name} role={user?.role} />

            <div className="offers-container-unique">
                <div className="offers-header-unique">
                    <h2>All Offers</h2>
                    <p>Discover high-converting offers for your traffic.</p>
                </div>

                {/* --- Filters Section --- */}
                <div className="filters-card-unique">

                    {/* Search Bar */}
                    <div className="search-row-unique">
                        <div className="search-input-group-unique">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                placeholder="Search by Offer Name or ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Filter Dropdowns & Tags */}
                    <div className="filter-controls-unique">

                        {/* Type Dropdown */}
                        <div className="dropdown-container-unique">
                            <button
                                className={`dropdown-btn-unique ${showTypeDrop ? 'active' : ''}`}
                                onClick={() => { setShowTypeDrop(!showTypeDrop); setShowCatDrop(false); }}
                            >
                                Filter Type <span>▼</span>
                            </button>
                            {showTypeDrop && (
                                <div className="dropdown-menu-unique">
                                    {uniqueTypes.map(type => (
                                        <div
                                            key={type}
                                            className={`dropdown-item-unique ${selectedTypes.includes(type) ? 'selected' : ''}`}
                                            onClick={() => toggleFilter(type, selectedTypes, setSelectedTypes)}
                                        >
                                            {type}
                                            {selectedTypes.includes(type) && <span className="check-mark">✓</span>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Category Dropdown */}
                        <div className="dropdown-container-unique">
                            <button
                                className={`dropdown-btn-unique ${showCatDrop ? 'active' : ''}`}
                                onClick={() => { setShowCatDrop(!showCatDrop); setShowTypeDrop(false); }}
                            >
                                Filter Category <span>▼</span>
                            </button>
                            {showCatDrop && (
                                <div className="dropdown-menu-unique">
                                    {uniqueCats.map(cat => (
                                        <div
                                            key={cat}
                                            className={`dropdown-item-unique ${selectedCats.includes(cat) ? 'selected' : ''}`}
                                            onClick={() => toggleFilter(cat, selectedCats, setSelectedCats)}
                                        >
                                            {cat}
                                            {selectedCats.includes(cat) && <span className="check-mark">✓</span>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Active Tags Display */}
                    {(selectedTypes.length > 0 || selectedCats.length > 0) && (
                        <div className="active-tags-row-unique">
                            {selectedTypes.map(t => (
                                <span key={t} className="filter-tag-unique">
                                    Type: {t} <i onClick={() => toggleFilter(t, selectedTypes, setSelectedTypes)}>&times;</i>
                                </span>
                            ))}
                            {selectedCats.map(c => (
                                <span key={c} className="filter-tag-unique">
                                    Cat: {c} <i onClick={() => toggleFilter(c, selectedCats, setSelectedCats)}>&times;</i>
                                </span>
                            ))}
                            <span
                                className="clear-all-unique"
                                onClick={() => { setSelectedTypes([]); setSelectedCats([]); }}
                            >
                                Clear All
                            </span>
                        </div>
                    )}
                </div>

                {/* --- Data Table --- */}
                <div className="table-card-unique">
                    <div className="table-responsive-unique">
                        <table className="offers-table-unique">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Offer Details</th>
                                    <th>Category</th>
                                    <th>Type</th>
                                    <th>Payout</th>
                                    <th style={{ textAlign: 'right' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOffers.length > 0 ? (
                                    filteredOffers.map((offer) => (
                                        <tr key={offer.id}>
                                            <td className="id-cell">#{offer.id}</td>
                                            <td className="name-cell">
                                                <div className="offer-info-wrapper">
                                                    <img
                                                        src={offer.logo || 'https://cdn-icons-png.flaticon.com/512/3673/3673024.png'}
                                                        alt="logo"
                                                        className="table-offer-logo"
                                                    />
                                                    <span>{offer.name}</span>
                                                </div>
                                            </td>
                                            <td><span className="badge-unique cat">{offer.category}</span></td>
                                            <td><span className="badge-unique type">{offer.type}</span></td>
                                            <td className="payout-cell">{offer.payout}</td>

                                            <td style={{ textAlign: 'right' }}>
                                                {/* ACTION LOGIC */}
                                                {offer.approved === true ? (
                                                    <button
                                                        className="action-btn-unique view"
                                                        onClick={() => navigate(`/offers/${offer.id}`)}
                                                    >
                                                        View Details
                                                    </button>
                                                ) : offer.approved === 'pending' ? (
                                                    <button className="action-btn-unique pending" disabled>
                                                        🕒 Pending
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="action-btn-unique apply"
                                                        onClick={() => handleApply(offer.id)}
                                                        disabled={applyingId === offer.id}
                                                    >
                                                        {applyingId === offer.id ? '...' : 'Apply Request'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="no-data-cell">
                                            No offers found matching your filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ApprovedOffers;